// Shared entrance-animation tokens for discovery result tiles (Similar grid,
// Pairs-With rows) and the search-interstitial orbit, so their stagger/easing
// stay in sync across packages.
export const ENTRANCE_EASING = [0.25, 0.1, 0.12, 1] as const
export const ENTRANCE_STAGGER_STEP = 0.04
export const ENTRANCE_INITIAL_DELAY = 0.05

// The number of orbiting tiles in the search interstitial. Single source of truth:
// SearchInterstitial renders exactly this many cards, buildInterstitialImagePool
// tiles its pool to this length, and the exit-duration derivation below assumes the
// last card is index (count - 1). Raising it here keeps all three in lockstep, so
// the unmount timer can never assume a stale count and reveal the page mid-exit.
export const INTERSTITIAL_CARD_COUNT = 10

// The orbit's first cards show the product being matched; results fill the rest,
// then take over these cards too (buildInterstitialImagePool).
export const INTERSTITIAL_SOURCE_CARD_COUNT = 3
// Every image the orbit reveals: each card once, plus the product cards' results.
export const INTERSTITIAL_REVEAL_COUNT =
  INTERSTITIAL_CARD_COUNT + INTERSTITIAL_SOURCE_CARD_COUNT

// The interstitial's loading sequence: three phrases, one per step, while the
// orbit reveals its images one by one. Its length is the overlay's floor
// (useSearchInterstitial), so every phrase is always seen and the last image has
// settled as it ends. `?loaderMs=` overrides it (useInterstitialTiming).
export const INTERSTITIAL_STEP_COUNT = 3
export const INTERSTITIAL_DEFAULT_MS = 2100
export const INTERSTITIAL_MIN_MS = 500
export const INTERSTITIAL_MAX_MS = 15000
// Parts of the default sequence; a different length scales them in proportion.
const FIRST_REVEAL_MS = 250
const IMAGE_REVEAL_MS = 400

export type InterstitialTiming = {
  // The whole sequence, and the overlay's floor.
  durationMs: number
  // How long each phrase shows.
  stepMs: number
  // When the first orbit image starts, and the gap to each next one.
  firstRevealMs: number
  revealIntervalMs: number
  // How long one image takes to settle into place.
  revealMs: number
}

// The sequence paced to `durationMs`, clamped to a range that stays readable.
export function interstitialTiming(
  durationMs: number = INTERSTITIAL_DEFAULT_MS,
): InterstitialTiming {
  const duration = Math.min(
    Math.max(durationMs, INTERSTITIAL_MIN_MS),
    INTERSTITIAL_MAX_MS,
  )
  const scale = duration / INTERSTITIAL_DEFAULT_MS
  const firstRevealMs = FIRST_REVEAL_MS * scale
  const revealMs = IMAGE_REVEAL_MS * scale

  return {
    durationMs: duration,
    stepMs: duration / INTERSTITIAL_STEP_COUNT,
    firstRevealMs,
    revealIntervalMs:
      (duration - firstRevealMs - revealMs) / (INTERSTITIAL_REVEAL_COUNT - 1),
    revealMs,
  }
}

// Interstitial exit choreography. The orbiting tiles fade + shrink out in reverse
// entrance order (see CirclingElements). These are the single source of truth so
// the overlay's unmount (useSearchInterstitial) is always derived from — and can
// never fire before — the animation actually finishes.
export const INTERSTITIAL_EXIT_CARD_DURATION = 0.2
export const INTERSTITIAL_EXIT_CARD_STAGGER = 0.01
// A frame of headroom over the last card's completion, so React's commit latency
// and Motion's animation start can never let the unmount timer win the race and
// reveal the page mid-exit.
const INTERSTITIAL_EXIT_SAFETY_MS = 40
// The last card leaves at stagger*(count-1)+duration.
export const INTERSTITIAL_EXIT_DURATION_MS =
  Math.round(
    (INTERSTITIAL_EXIT_CARD_STAGGER * (INTERSTITIAL_CARD_COUNT - 1) +
      INTERSTITIAL_EXIT_CARD_DURATION) *
      1000,
  ) + INTERSTITIAL_EXIT_SAFETY_MS
